import { useState } from "react";
import { Bot, Volume2, VolumeX } from "lucide-react";
import { API, mediaUrl } from "@/lib/api";
import { useSiteConfig } from "@/components/world1/Shell";

export const useCopy = () => {
  const cfg = useSiteConfig();
  return { c: cfg?.helpers_site || {}, cfg };
};

const src = (slot, v, w, f) => `${API}/public/helpers-img/${slot}?w=${w}&f=${f}&v=${v}`;
const set = (slot, v, f) => [480, 960, 1600].map((w) => `${src(slot, v, w, f)} ${w}w`).join(", ");

// Uploaded slot image as responsive WebP (JPEG fallback); a soft pastel placeholder when the slot is empty.
// fit="contain" shows the whole picture; focal point (object-position) comes from Admin → Content.
export function HImg({ slot, alt, className = "", sizes = "(min-width: 1024px) 50vw, 100vw", eager = false, fit = "cover", natural = false }) {
  const { cfg } = useCopy();
  const v = cfg?.helpers_images?.[slot];
  const [broken, setBroken] = useState(false);
  if (!v || broken) {
    return (
      <div data-testid={`helpers-img-${slot}`} data-empty="1" role="img" aria-label={alt}
        className={`h-placeholder flex items-center justify-center ${className}`}>
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/70 text-[color:var(--h-mint-deep)] shadow-sm"><Bot size={26} /></span>
      </div>
    );
  }
  return (
    <picture>
      <source type="image/webp" srcSet={set(slot, v, "webp")} sizes={sizes} />
      <img data-testid={`helpers-img-${slot}`} src={src(slot, v, 960, "jpg")} srcSet={set(slot, v, "jpg")} sizes={sizes} alt={alt}
        loading={eager ? "eager" : "lazy"} decoding="async" onError={() => setBroken(true)}
        style={{ objectPosition: cfg?.helpers_focus?.[slot] || "50% 50%" }}
        className={`${fit === "contain" ? "object-contain" : "object-cover"} ${className} ${natural ? "max-lg:!aspect-auto max-lg:h-auto" : ""}`} />
    </picture>
  );
}

// Circular headshot shown as uploaded; during the wireframe flicker its cyan face-mesh twin shows on top.
export function Portrait({ slot, name, fallback }) {
  const { cfg } = useCopy();
  const v = cfg?.helpers_images?.[slot];
  const pos = { objectPosition: cfg?.helpers_focus?.[slot] || "50% 50%" };
  return (
    <div data-testid={`helpers-portrait-${slot}`} className="relative mb-4 flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-[color:var(--h-mint-soft)] text-2xl font-semibold text-[color:var(--h-mint-deep)]">
      {v ? (
        <>
          <img src={`${mediaUrl(slot)}?v=${v}`} alt={name} style={pos} className="h-full w-full object-cover" />
          <img src={`${API}/public/helpers-wire/${slot}?v=${v}`} alt="" aria-hidden style={pos} className="h-founder-wire absolute inset-0 h-full w-full object-cover" />
        </>
      ) : fallback}
    </div>
  );
}

// "Global Headquarters · 48°52′36″S 123°23′36″W" — coordinates link to the map.
export function HQLine({ className = "", testid = "helpers-hq" }) {
  const { c } = useCopy();
  if (!c.hq_coords) return null;
  return (
    <p data-testid={testid} className={className}>
      {c.hq_label} · <a href={c.hq_map_url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-map-link`} className="whitespace-nowrap underline decoration-[color:var(--h-line)] underline-offset-2 hover:text-[color:var(--h-ink)]">{c.hq_coords}</a>
    </p>
  );
}

export function SpokesVideo() {
  const { c, cfg } = useCopy();
  const [muted, setMuted] = useState(true);
  if (!cfg?.helpers_images?.helpers_video_spokesbot) return <HImg slot="helpers_video_spokesbot" alt={c.video_title} className="aspect-video w-full rounded-[2rem]" />;
  return (
    <div className="relative overflow-hidden rounded-[2rem] bg-white shadow-[0_30px_60px_-30px_rgba(31,138,100,.45)]">
      <video data-testid="helpers-spokesbot-video" src={mediaUrl("helpers_video_spokesbot")} muted={muted} autoPlay loop playsInline className="aspect-video w-full object-cover" />
      <button type="button" data-testid="helpers-video-sound-button" onClick={() => setMuted((m) => !m)}
        className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-[color:var(--h-ink)] shadow-md backdrop-blur transition-transform hover:scale-105">
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />} {muted ? "Tap for sound" : "Sound on"}
      </button>
    </div>
  );
}

export function JoinButton({ children, onClick, variant = "solid", testid }) {
  const cls = variant === "solid"
    ? "bg-[color:var(--h-mint-deep)] text-white hover:bg-[color:var(--h-mint-ink)] shadow-[0_10px_24px_-10px_rgba(31,138,100,.7)]"
    : "bg-white text-[color:var(--h-ink)] ring-1 ring-[color:var(--h-line)] hover:bg-[color:var(--h-peach-soft)]";
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`h-btn inline-flex items-center justify-center rounded-full px-7 py-3.5 text-[15px] font-semibold ${cls}`}>{children}</button>
  );
}

export function Kicker({ children }) {
  return <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--h-mint-deep)]">{children}</p>;
}

export const initials = (name) => name.replace(/\b(Dr|Gen|Ret|Mr|Ms|Mrs)\.|[()]/g, "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

export const split = (line, n) => {
  const parts = String(line || "").split("|").map((x) => x.trim());
  return Array.from({ length: n }, (_, i) => parts[i] || "");
};
